import { Box, Stack, Typography } from "@mui/material";
import { RichTreeView } from "@mui/x-tree-view/RichTreeView";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { MapEditorFeatureCreateOptionsSelect_ObservationFragment } from "../../fragments.generated";
import { getAutoExpandedItems, mapToTreeNodes } from "./utils";

type ObservationTypeTreeProps = {
    researchTypes: MapEditorFeatureCreateOptionsSelect_ObservationFragment[];
    selectedObservationTypeId: string | null;
    onSelectionChange: (itemId: string | null) => void;
};

export function ObservationTypeTree({
    researchTypes,
    selectedObservationTypeId,
    onSelectionChange,
}: ObservationTypeTreeProps) {
    const { t } = useTranslation("mapEditor");

    // Manage accordion expansion internally
    const [expandedItems, setExpandedItems] = useState<string[]>([]);

    // Derive tree items from research types (no filtering - parent does that)
    const treeItems = useMemo(() => mapToTreeNodes(researchTypes), [researchTypes]);

    // Auto-expand when tree items change
    useEffect(() => {
        const autoExpanded = getAutoExpandedItems(treeItems);
        setExpandedItems(autoExpanded);
    }, [treeItems]);

    // Handle expansion changes - accordion style (only one expanded at a time)
    const handleExpandedItemsChange = (_: unknown, itemIds: string[]) => {
        // If expanding a new item, collapse all others
        if (itemIds.length > expandedItems.length) {
            // User is expanding - find the newly expanded item
            const newlyExpanded = itemIds.find(id => !expandedItems.includes(id));
            setExpandedItems(newlyExpanded ? [newlyExpanded] : []);
        } else {
            // User is collapsing
            setExpandedItems(itemIds);
        }
    };

    // Handle selection changes
    const handleSelectionChange = (_: unknown, itemIds: string | string[] | null) => {
        // Handle both single string and array (for type safety)
        const selectedId = Array.isArray(itemIds) ? itemIds[0] : itemIds;
        onSelectionChange(selectedId ?? null);
    };

    return (
        <Stack rowGap={1}>
            <Typography variant="subtitle2">
                {t("mapEditorFeatureCreateDialog.observationSection.labelSelectObservationType")}
            </Typography>
            <Box
                sx={theme => ({
                    maxHeight: 300,
                    overflowY: "auto",
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 1,
                })}
            >
                <RichTreeView
                    items={treeItems}
                    selectedItems={selectedObservationTypeId ?? undefined}
                    onSelectedItemsChange={handleSelectionChange}
                    expandedItems={expandedItems}
                    onExpandedItemsChange={handleExpandedItemsChange}
                />
            </Box>
        </Stack>
    );
}
